import { CalendarCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { VerifiedIcon, TeamIcon } from "@/components/icons/domain";
import { PremiumIcon, HeartIcon } from "@/components/icons/status";

const BENEFITS = [
  {
    icon: CalendarCheck,
    title: "Plannings en minutes",
    description: "Glisser-déposer, suggestions auto, exports PDF prêts à afficher.",
  },
  {
    icon: VerifiedIcon,
    title: "Conforme Code du travail",
    description: "Repos, heures sup, IDCC 1505 — les règles sont gérées pour vous.",
  },
  {
    icon: TeamIcon,
    title: "Équipe synchronisée",
    description: "Vos salariés voient leur planning en temps réel et signent leurs heures.",
  },
];

interface AuthBenefitsPanelProps {
  className?: string;
}

export function AuthBenefitsPanel({ className }: AuthBenefitsPanelProps) {
  return (
    <div className={cn("animate-fade-in", className)}>
      {/* Eyebrow */}
      <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-3 py-1 text-xs font-semibold text-primary">
        <span className="w-1.5 h-1.5 rounded-full bg-primary motion-safe:animate-pulse" />
        Essai gratuit · 14 jours · sans carte
      </span>

      {/* Titre Fraunces */}
      <h2 className="mt-5 text-4xl xl:text-5xl tracking-tight leading-[1.05] text-foreground font-display italic font-medium">
        Bienvenue dans la nouvelle façon de{" "}
        <span className="gradient-text not-italic font-semibold">planifier</span>.
      </h2>
      <p className="mt-4 text-base lg:text-lg text-muted-foreground leading-relaxed max-w-md">
        Equiply remplace les tableaux Excel et les SMS à 22h. Vous reprenez le contrôle de votre temps.
      </p>

      {/* Liste de bénéfices — desktop */}
      <ul className="hidden sm:flex flex-col gap-4 mt-8" data-stagger>
        {BENEFITS.map((b) => (
          <li
            key={b.title}
            className="flex items-start gap-3.5 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-left-2 fill-mode-forwards duration-slow"
          >
            <span
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-accent/15 border border-primary/15 flex items-center justify-center text-primary"
              aria-hidden="true"
            >
              <b.icon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground leading-tight">{b.title}</p>
              <p className="text-sm text-muted-foreground leading-snug mt-0.5">{b.description}</p>
            </div>
          </li>
        ))}
      </ul>

      {/* Liste de bénéfices — mobile (chips scroll horizontal) */}
      <ul
        className="sm:hidden mt-6 -mx-4 px-4 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label="Bénéfices Equiply"
      >
        {BENEFITS.map((b) => (
          <li
            key={b.title}
            className="snap-start flex-shrink-0 w-[78%] rounded-2xl border border-border/60 bg-card p-4 flex items-start gap-3"
          >
            <span
              className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-primary/15 to-accent/15 border border-primary/15 flex items-center justify-center text-primary"
              aria-hidden="true"
            >
              <b.icon className="w-4 h-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground leading-tight">{b.title}</p>
              <p className="text-xs text-muted-foreground leading-snug mt-0.5">{b.description}</p>
            </div>
          </li>
        ))}
      </ul>

      {/* Signature */}
      <div className="hidden lg:flex mt-10">
        <div className="inline-flex items-center gap-2 rounded-full bg-background border border-border/50 px-3.5 py-2 text-xs font-medium text-foreground/80 shadow-soft">
          <HeartIcon className="w-3.5 h-3.5 text-destructive fill-destructive" />
          Conçu par un ancien gérant Carrefour Express
          <PremiumIcon className="w-3.5 h-3.5 text-primary" />
        </div>
      </div>
    </div>
  );
}
